'use client'
import { useListAccountNoticesQuery } from '@web/gen/user'
import * as React from 'react'
import { Toast } from '@web/components/toaster'
import type { MyAccountNotice } from 'codegen/types'

export function MyAccountNotices() {
  const { data, isPending } = useListAccountNoticesQuery()
  const [notices, setNotices] = React.useState<(MyAccountNotice & { id: string })[]>([])

  React.useEffect(() => {
    if (!data?.listAccountNotices?.notices) {
      return
    }
    setNotices(prev => {
      // Dedupe against both already-shown notices and duplicates within the
      // incoming batch itself. The backend can return the same notice multiple
      // times in a single response, so we add each key to `seenKeys` as we go
      // to keep only the first occurrence of each message + type.
      const seenKeys = new Set(prev.map(n => `${n.type}::${n.message}`))
      const newOnes: (MyAccountNotice & { id: string })[] = []
      for (const notice of data.listAccountNotices.notices) {
        const key = `${notice.type}::${notice.message}`
        if (seenKeys.has(key)) {
          continue
        }
        seenKeys.add(key)
        newOnes.push({
          id: crypto.randomUUID(),
          message: notice.message,
          type: notice.type as MyAccountNotice['type'],
        })
      }
      return [...prev, ...newOnes]
    })
  }, [data])

  const dismissNotice = (id: string) => {
    setNotices(prev => prev.filter(n => n.id !== id))
  }

  if (isPending || notices.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col gap-4">
      {notices.map(({ id, message, type }) => (
        <Toast
          key={id}
          message={message}
          type={type as 'error' | 'success' | 'notice' | 'loading'}
          onClose={() => dismissNotice(id)}
          standalone
        />
      ))}
    </div>
  )
}
